import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreatePrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";

type DefaultSkill = {
    id: string;
    name: string;
    trigger: string;
    description: string;
    prompt: string;
};

const DEFAULT_SKILLS: DefaultSkill[] = [
    {
        id: "pr",
        name: "Pull Request",
        trigger: "pull-request",
        description: "Raise a draft pull request with a standard template",
        prompt: "Raise a draft PR for a branch starting with my initials following this template. Get my initials from the configured git username. Check for manual files changes before creating the PR description. \\n## Description\\n<!-- Describe your changes in detail -->\\n## Related Issue(s)\\n<!-- List the issue(s) this PR solves -->\\nFixes <issue-ref>\\n## How to test\\n<!-- Provide steps to test this PR -->",
    },
    {
        id: "documentation",
        name: "Documentation",
        trigger: "docs",
        description: "Generate documentation for functions and code",
        prompt: "Generate comprehensive documentation for the following code, including:\\n- Function/method descriptions\\n- Parameter explanations\\n- Return value descriptions\\n- Usage examples",
    },
    {
        id: "explain-code",
        name: "Explain Code",
        trigger: "explain",
        description: "Explain how code works in simple terms",
        prompt: "Explain how this code works in simple, easy-to-understand terms. Break down complex concepts and provide examples where helpful.",
    },
    {
        id: "catch-up",
        name: "Catch Up",
        trigger: "catch-up",
        description: "Makes Ona understand the changes in this environment compared to main",
        prompt: "Understand the changes in this environment compared to main. What files have changed and why? What's the user's intent?",
    },
];

type ImportDefaultSkillsDialogProps = {
    open: boolean;
    onClose: () => void;
};

export const ImportDefaultSkillsDialog: FC<ImportDefaultSkillsDialogProps> = ({ open, onClose }) => {
    const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set(DEFAULT_SKILLS.map((skill) => skill.id)));
    const [isImporting, setIsImporting] = useState(false);

    const createPrompt = useCreatePrompt();
    const { toast } = useToast();

    const toggleSkill = useCallback((skillId: string) => {
        setSelectedSkills((prev) => {
            const next = new Set(prev);
            if (next.has(skillId)) {
                next.delete(skillId);
            } else {
                next.add(skillId);
            }
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        if (selectedSkills.size === DEFAULT_SKILLS.length) {
            setSelectedSkills(new Set());
        } else {
            setSelectedSkills(new Set(DEFAULT_SKILLS.map((skill) => skill.id)));
        }
    }, [selectedSkills.size]);

    const handleImport = useCallback(async () => {
        if (selectedSkills.size === 0) {
            return;
        }

        setIsImporting(true);
        const skillsToImport = DEFAULT_SKILLS.filter((skill) => selectedSkills.has(skill.id));

        let importedCount = 0;

        // Import skills sequentially to avoid race conditions
        for (const skill of skillsToImport) {
            try {
                await createPrompt.mutateAsync({
                    name: skill.name,
                    description: skill.description,
                    prompt: skill.prompt,
                    isCommand: true, // Keep for backward compatibility
                    isSkill: true, // Mark as skill
                    command: skill.trigger,
                });
                importedCount++;
            } catch (error) {
                // Silently skip skills that already exist
                const errorMessage = formatError(error);
                const isDuplicate =
                    errorMessage.toLowerCase().includes("already exists") ||
                    errorMessage.toLowerCase().includes("unique");
                if (!isDuplicate) {
                    console.error(`Failed to import ${skill.name}:`, error);
                }
            }
        }

        setIsImporting(false);

        if (importedCount > 0) {
            toast({
                title: "Skills imported successfully",
                description: `${importedCount} skill${importedCount > 1 ? "s" : ""} imported`,
            });
        }

        onClose();
    }, [selectedSkills, createPrompt, toast, onClose]);

    const allSelected = selectedSkills.size === DEFAULT_SKILLS.length;
    const someSelected = selectedSkills.size > 0 && selectedSkills.size < DEFAULT_SKILLS.length;

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="import-default-skills-modal"
                data-track-location={TrackLocations.ImportDefaultSkillsDialog}
            >
                <DialogHeader>
                    <DialogTitle>Import Default Skills</DialogTitle>
                    <DialogDescription>
                        Select the default skills you want to import into your organization
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <div className="flex items-center space-x-2 border-b border-border-base pb-3">
                        <Checkbox
                            id="select-all"
                            checked={someSelected ? "indeterminate" : allSelected}
                            onCheckedChange={toggleAll}
                            data-testid="select-all-checkbox"
                        />
                        <Label htmlFor="select-all" className="cursor-pointer font-medium">
                            Select All
                        </Label>
                    </div>

                    <div className="max-h-[400px] space-y-3 overflow-y-auto">
                        {DEFAULT_SKILLS.map((skill) => (
                            <div
                                key={skill.id}
                                className="flex items-start space-x-3 rounded-lg border border-border-base p-3 hover:bg-surface-hover"
                            >
                                <Checkbox
                                    id={skill.id}
                                    checked={selectedSkills.has(skill.id)}
                                    onCheckedChange={() => toggleSkill(skill.id)}
                                    data-testid={`skill-checkbox-${skill.id}`}
                                    className="mt-1"
                                />
                                <div className="flex-1 space-y-1">
                                    <Label htmlFor={skill.id} className="cursor-pointer font-medium">
                                        {skill.name}
                                        <span className="ml-2 text-sm font-normal text-content-muted">
                                            /{skill.trigger}
                                        </span>
                                    </Label>
                                    <p className="text-sm text-content-secondary">{skill.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isImporting}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleImport}
                        disabled={selectedSkills.size === 0 || isImporting}
                        loading={isImporting}
                        data-testid="import-skills-button"
                        data-tracking-id="import-default-skills"
                    >
                        Import{selectedSkills.size > 0 && <span> ({selectedSkills.size})</span>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
